import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { CircularProgress } from '@mui/material';
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { WorkspaceContext } from '../../context/WorkspaceContext';

const CrmApiAccess = () => {
  const { companyId } = useContext(WorkspaceContext);
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState(null);

  const firebaseProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  const webhookUrl = firebaseProjectId
    ? `POST https://us-central1-${firebaseProjectId}.cloudfunctions.net/ingestCrmLead`
    : 'POST https://us-central1-[YOUR-PROJECT-ID].cloudfunctions.net/ingestCrmLead';

  useEffect(() => {
    const fetchApiKey = async () => {
      if (!companyId) {
        setLoading(false);
        setError('Company ID not available. Please ensure you are logged in and have a valid workspace.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const q = query(
          collection(db, 'companies', companyId, 'api_keys'),
          where('status', '==', 'active'),
          limit(1),
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setApiKey({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
        } else {
          setApiKey(null);
        }
      } catch (err) {
        console.error('Error fetching API key:', err);
        setError('Failed to fetch API key. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchApiKey();
  }, [companyId]);

  const generateApiKey = async (rotate = false) => {
    if (!companyId) {
      setError('Company ID not found. Cannot generate key.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (rotate && apiKey) {
        const oldKeyRef = doc(db, 'companies', companyId, 'api_keys', apiKey.id);
        await updateDoc(oldKeyRef, { status: 'inactive' });
      }

      const newKey = `spm_live_${window.crypto.randomUUID().replace(/-/g, '')}`;
      const newKeyRef = await addDoc(collection(db, 'companies', companyId, 'api_keys'), {
        key: newKey,
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setApiKey({ id: newKeyRef.id, key: newKey, status: 'active', createdAt: new Date() });
      setCopySuccess(false);
      setShowKey(false);
    } catch (err) {
      console.error('Error generating API key:', err);
      setError('Failed to generate API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (apiKey?.key) {
      navigator.clipboard.writeText(apiKey.key);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (!companyId) {
    return (
      <Alert severity="error">
        Company ID not available. Please ensure you are logged in and have a valid workspace.
      </Alert>
    );
  }

  return (
    <Box>
      {loading && (
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography color="text.secondary">Loading CRM webhook key…</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !apiKey && (
        <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
          <Typography color="text.secondary">
            No active CRM webhook API key found.
          </Typography>
          <Button variant="contained" onClick={() => generateApiKey()} disabled={loading}>
            Generate webhook key
          </Button>
        </Stack>
      )}

      {!loading && apiKey && (
        <Stack spacing={2}>
          <TextField
            label="Webhook API key"
            type={showKey ? 'text' : 'password'}
            value={apiKey.key}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowKey(!showKey)}
                    aria-label={showKey ? 'Hide key' : 'Show key'}
                  >
                    {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={copyToClipboard} aria-label="Copy to clipboard">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {copySuccess && (
            <Typography variant="caption" color="success.main">
              Copied to clipboard.
            </Typography>
          )}

          <TextField
            label="Webhook URL"
            value={webhookUrl}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            helperText="Send POST requests to this URL with your CRM lead payload."
          />

          <Button
            variant="outlined"
            color="warning"
            onClick={() => generateApiKey(true)}
            disabled={loading}
            sx={{ alignSelf: 'flex-start' }}
          >
            Rotate webhook key
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default CrmApiAccess;
